import React from 'react';
import { useParams, Link } from 'react-router-dom';
import './Legal.css';

const legalPages = {
  terms: {
    title: 'Terms & Conditions',
    updated: '4 March 2026',
    content: [
      {
        heading: 'Introduction',
        text: 'These Terms and Conditions govern your use of this website operated by Lossos & Co. Limited (Company Number 05820010) ("we", "us", or "our").\n\nBy accessing or using this website, you agree to comply with these Terms and Conditions. If you do not agree with any part of these terms, you should not use this website.',
      },
      {
        heading: 'Website Use',
        text: 'You agree to use this website only for lawful purposes. You must not:',
        list: [
          'Attempt to gain unauthorised access to the website or its systems',
          'Introduce viruses, malware, or harmful code',
          'Use the website for fraudulent or unlawful activities',
          'Interfere with the functionality or security of the website',
        ],
      },
      {
        heading: 'Intellectual Property',
        text: 'All content on this website, including text, graphics, logos, images, and design, is the property of Lossos & Co. Limited unless otherwise stated.\n\nThis content is protected by copyright and intellectual property laws. You may not copy, reproduce, distribute, or modify any content without written permission.',
      },
      {
        heading: 'Accuracy of Information',
        text: 'We aim to keep the information on this website accurate and up to date. However, we do not guarantee that all information is complete, accurate, or current.\n\nWe may update or change content on this website at any time without notice.',
      },
      {
        heading: 'Limitation of Liability',
        text: 'To the fullest extent permitted by law, Lossos & Co. Limited shall not be liable for any loss or damage arising from:',
        list: [
          'Use of this website',
          'Inability to access the website',
          'Reliance on information contained on the website',
        ],
        afterList: 'Nothing in these terms excludes liability where it would be unlawful to do so.',
      },
      {
        heading: 'Governing Law',
        text: 'These Terms and Conditions are governed by the laws of England and Wales.',
      },
      {
        heading: 'Contact',
        text: 'If you have questions about these Terms and Conditions, please contact:\n\nLossos & Co. Limited\nEmail: info@thelpacompany.com',
      },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    updated: '4 March 2026',
    content: [
      {
        heading: 'Who We Are',
        text: 'This website is operated by Lossos & Co. Limited.\n\nIf you have questions about this policy or your personal data, please contact:\n\nEmail: info@thelpacompany.com',
      },
      {
        heading: 'Information We Collect',
        text: 'We may collect the following information:',
        list: [
          'Name',
          'Email address',
          'Contact information submitted through forms',
          'Technical information such as IP address, browser type, device information, and pages visited',
        ],
      },
      {
        heading: 'How We Use Information',
        text: 'We use collected information to:',
        list: [
          'Operate and maintain the website',
          'Respond to enquiries',
          'Improve website functionality and performance',
          'Maintain website security',
        ],
      },
      {
        heading: 'Data Sharing',
        text: 'We may share data with trusted third-party service providers that help operate the website, such as hosting providers or analytics services.\n\nWe do not sell personal data.',
      },
      {
        heading: 'Your Rights',
        text: 'Under UK GDPR, you may have the right to:',
        list: [
          'Request access to your personal data',
          'Request correction of inaccurate data',
          'Request deletion of your personal data',
          'Restrict or object to processing',
          'Withdraw consent where applicable',
        ],
        afterList: 'To exercise these rights, contact: info@thelpacompany.com',
      },
    ],
  },
  cookies: {
    title: 'Cookie Policy',
    updated: '4 March 2026',
    content: [
      {
        heading: 'What Are Cookies',
        text: 'Cookies are small text files stored on your device when you visit a website.\n\nThey help websites function properly and improve the user experience.',
      },
      {
        heading: 'Types of Cookies We Use',
        text: '',
        subsections: [
          {
            subheading: 'Essential cookies',
            text: 'These cookies are required for the website to function properly.',
          },
          {
            subheading: 'Analytics cookies',
            text: 'These help us understand how visitors use the website so we can improve it.',
          },
          {
            subheading: 'Marketing cookies',
            text: 'These may help measure advertising effectiveness or provide relevant content.',
          },
        ],
      },
      {
        heading: 'Managing Cookies',
        text: 'You can control or disable cookies through your browser settings or through cookie preferences on the website.',
      },
    ],
  },
  disclaimer: {
    title: 'Website Disclaimer',
    updated: '4 March 2026',
    content: [
      {
        heading: '',
        text: 'The information provided on this website is for general information purposes only.\n\nWhile we aim to keep the information accurate and up to date, Lossos & Co. Limited (Company Number 05820010) makes no guarantees regarding completeness, reliability, or accuracy.\n\nAny reliance you place on the information on this website is strictly at your own risk.\n\nThis website may contain links to third-party websites. We have no control over the content of those sites and accept no responsibility for them.',
      },
    ],
  },
};

export default function Legal() {
  const { page } = useParams();
  const data = legalPages[page];

  if (!data) {
    return (
      <div className="legal">
        <div className="legal__hero">
          <div className="legal__hero-inner">
            <h1>Page Not Found</h1>
          </div>
        </div>
        <div className="legal__body">
          <div className="legal__body-inner">
            <p>The page you are looking for does not exist.</p>
            <Link to="/" className="btn btn-primary" style={{ marginTop: 24 }}>Return Home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="legal">
      <div className="legal__hero">
        <div className="legal__hero-inner">
          <div className="legal__breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <span>{data.title}</span>
          </div>
          <h1>{data.title}</h1>
          <p className="legal__updated">Last updated: {data.updated}</p>
        </div>
      </div>
      <div className="legal__body">
        <div className="legal__body-inner">
          {data.content.map((section, i) => (
            <div className="legal__section" key={i}>
              {section.heading && <h2>{section.heading}</h2>}
              {section.text && section.text.split('\n\n').map((para, j) => (
                <p key={j}>{para}</p>
              ))}
              {section.list && (
                <ul>
                  {section.list.map((item, k) => (
                    <li key={k}>{item}</li>
                  ))}
                </ul>
              )}
              {section.afterList && <p>{section.afterList}</p>}
              {section.subsections && section.subsections.map((sub, m) => (
                <div className="legal__subsection" key={m}>
                  <h3>{sub.subheading}</h3>
                  <p>{sub.text}</p>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
